import { useState, useRef, useCallback } from "react";
import { RoomService } from "../../services/RoomService";
import { Room, AppointmentDetails, FilterState } from "../../types";
import { AppError } from "../../errors/AppError";

interface UseRoomSearchResult {
  rooms: Room[];
  loading: boolean;
  error: AppError | null;
  searchRooms: (
    details: AppointmentDetails,
    filters: FilterState,
    officeId?: string | null
  ) => Promise<void>;
  clearError: () => void;
}

const DEBOUNCE_MS = 500;

export function useRoomSearch(): UseRoomSearchResult {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const isSearchingRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchParamsRef = useRef<string | null>(null);
  const pendingSearchRef = useRef<(() => Promise<void>) | null>(null);

  const searchRooms = useCallback(
    async (
      details: AppointmentDetails,
      filters: FilterState,
      officeId?: string | null
    ): Promise<void> => {
      // Create a cache key from search parameters
      const cacheKey = JSON.stringify({
        start: details.start.toISOString(),
        end: details.end.toISOString(),
        capacity: filters.minCapacity,
        equipment: filters.selectedEquipment.sort(),
        officeId: officeId ?? null,
      });

      if (lastSearchParamsRef.current === cacheKey) {
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const executeSearch = async () => {
        isSearchingRef.current = true;
        lastSearchParamsRef.current = cacheKey;
        setLoading(true);
        setError(null);

        try {
          const fetchedRooms = await RoomService.searchRooms({
            date: details.start.toISOString().split("T")[0],
            startTime: details.start.toISOString(),
            endTime: details.end.toISOString(),
            capacity: filters.minCapacity,
            equipment: filters.selectedEquipment,
            attendees: [
              ...details.requiredAttendees,
              ...details.optionalAttendees,
            ],
            ...(officeId ? { officeId } : {}),
          });

          setRooms(fetchedRooms);
        } catch (err) {
          const appError = AppError.from(err);
          setError(appError);
          if (appError.type !== "auth") {
            lastSearchParamsRef.current = null;
          }
        } finally {
          setLoading(false);
          isSearchingRef.current = false;

          // Execute any pending search that came in while we were busy
          const pending = pendingSearchRef.current;
          if (pending) {
            pendingSearchRef.current = null;
            void pending();
          }
        }
      };

      if (isSearchingRef.current) {
        // A search is in progress — queue this one to run after it completes
        pendingSearchRef.current = executeSearch;
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        void executeSearch();
      }, DEBOUNCE_MS);
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { rooms, loading, error, searchRooms, clearError };
}
